import type { CSSProperties } from 'react'
import SelectionCheckBadge from './SelectionCheckBadge'

export interface SelectionPickerOption {
  value: string
  color?: string
  title: string
  hint?: string
  /** Optional illustration above title (desktop sell builder Būklė cards). */
  imageSrc?: string
  imageAlt?: string
}

interface SelectionPickerGridProps {
  options: readonly SelectionPickerOption[]
  value: string | null | undefined
  onChange: (value: string) => void
  ariaLabel: string
  /** Single column — better for longer hints (kit, shipment). */
  stack?: boolean
  /** History step compact pills — isolated from global condition-picker-card hover styles. */
  chipVariant?: 'history'
}

export default function SelectionPickerGrid({
  options,
  value,
  onChange,
  ariaLabel,
  stack = false,
  chipVariant,
}: SelectionPickerGridProps) {
  return (
    <div
      className={`condition-picker-grid${stack ? ' condition-picker-grid--stack' : ''}`}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((opt) => {
        const active = value === opt.value
        const color = opt.color ?? 'var(--accent)'
        if (chipVariant === 'history') {
          return (
            <button
              key={opt.value}
              type="button"
              className={`usage-duration-chip${active ? ' usage-duration-chip--active' : ''}`}
              aria-pressed={active}
              onClick={() => onChange(opt.value)}
            >
              <span className="usage-duration-chip__dot" aria-hidden />
              <span className="usage-duration-chip__title">{opt.title}</span>
            </button>
          )
        }
        return (
          <button
            key={opt.value}
            type="button"
            className={`condition-picker-card${active ? ' condition-picker-card--active' : ''}${
              opt.imageSrc ? ' condition-picker-card--illustrated' : ''
            }`}
            style={{ '--condition-color': color } as CSSProperties}
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
          >
            {opt.imageSrc ? (
              <span
                className={`condition-picker-card__illus${active ? ' condition-picker-card__illus--active' : ''}`}
                aria-hidden="true"
              >
                <img src={opt.imageSrc} alt="" draggable={false} />
                {active ? (
                  <SelectionCheckBadge anchored className="selection-check-badge--illus-anchor" />
                ) : null}
              </span>
            ) : (
              <span className="condition-picker-card__dot" aria-hidden />
            )}
            <span className="condition-picker-card__body">
              <span className="condition-picker-card__title">{opt.title}</span>
              {opt.hint ? <span className="condition-picker-card__hint">{opt.hint}</span> : null}
            </span>
            {active && !opt.imageSrc ? <SelectionCheckBadge anchored /> : null}
          </button>
        )
      })}
    </div>
  )
}
